/**
 * dateUtils.js
 * Centralised date / time helpers built on date-fns.
 * Replaces moment.js across the entire codebase.
 */
import {
  format,
  parse,
  isValid,
  isBefore,
  isAfter,
  isEqual,
  differenceInDays,
} from 'date-fns';

// ─── Simple formatters ────────────────────────────────────────────────────────

/** "2024-01-31" → "31-01-2024" */
export const formatDateDMY = (date) => {
  if (!date) return '';
  try { return format(new Date(date), 'dd-MM-yyyy'); } catch { return ''; }
};

/** "2024-01-31T10:00:00" → "2024-01-31" */
export const formatDateYMD = (date) => {
  if (!date) return '';
  try { return format(new Date(date), 'yyyy-MM-dd'); } catch { return ''; }
};

/** Today as "2024-01-31" */
export const todayYMD = () => format(new Date(), 'yyyy-MM-dd');

/** Current time as "14:30" */
export const nowHHmm = () => format(new Date(), 'HH:mm');

/**
 * Format a date for API query strings (ISO-like with literal Z suffix).
 * Mirrors: moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
 */
export const formatDateTimeISO = (date) => {
  if (!date) return '';
  try { return format(new Date(date), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"); } catch { return ''; }
};

/**
 * Parse a value that was stored as "YYYY-MM-DD HH:mm:ss" and return "DD-MM-YYYY".
 * Mirrors: moment(value, 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY')
 */
export const parseAndFormatDMY = (value) => {
  if (!value) return '';
  try {
    const parsed = parse(String(value), 'yyyy-MM-dd HH:mm:ss', new Date());
    return isValid(parsed) ? format(parsed, 'dd-MM-yyyy') : '';
  } catch { return ''; }
};

// ─── Multi-format date parsing ────────────────────────────────────────────────

const DATE_FORMATS = [
  'yyyy-MM-dd',
  'MM/dd/yyyy',
  'dd-MM-yyyy',
  "yyyy-MM-dd'T'HH:mm:ss",
];

/**
 * Try to parse a raw value as a Date using multiple common formats.
 * Returns a `Date` on success or `null` on failure.
 *
 * Mirrors: moment(value, [moment.ISO_8601, "YYYY-MM-DD", …], true)
 */
export const tryParseDate = (value) => {
  if (!value) return null;
  // Native Date / ISO 8601
  const native = new Date(value);
  if (!isNaN(native.getTime())) return native;
  // Explicit format fallbacks
  const ref = new Date();
  for (const fmt of DATE_FORMATS) {
    try {
      const parsed = parse(String(value), fmt, ref);
      if (isValid(parsed)) return parsed;
    } catch { /* skip */ }
  }
  return null;
};

// ─── Time parsing ─────────────────────────────────────────────────────────────

// Stable reference date used as the base for time-only parsing
const TIME_BASE = new Date(2000, 0, 1);

/**
 * Parse a 24-hour time string ("HH:mm" or "HH:mm:ss").
 * Returns `{ date: Date, totalMinutes: number }` or `null`.
 *
 * Mirrors: moment(value, ["HH:mm","HH:mm:ss"], true)
 */
export const parseTime24 = (value) => {
  if (!value) return null;
  for (const fmt of ['HH:mm', 'HH:mm:ss']) {
    try {
      const parsed = parse(String(value), fmt, TIME_BASE);
      if (isValid(parsed)) {
        return {
          date: parsed,
          totalMinutes: parsed.getHours() * 60 + parsed.getMinutes(),
        };
      }
    } catch { /* skip */ }
  }
  return null;
};

/**
 * Parse a time string in 12-hour ("h:mm a") or 24-hour ("HH:mm") format.
 * Returns a `Date` anchored to a fixed base date, or `null`.
 *
 * Mirrors: moment(value, ["h:mm A","HH:mm"])
 */
export const parseTimeAny = (value) => {
  if (!value) return null;
  for (const fmt of ['h:mm a', 'HH:mm']) {
    try {
      const parsed = parse(String(value), fmt, TIME_BASE);
      if (isValid(parsed)) return parsed;
    } catch { /* skip */ }
  }
  return null;
};

// ─── Comparison helpers ───────────────────────────────────────────────────────

/**
 * Compare two time strings.
 * Returns: -1 (t1 before t2), 1 (t1 after t2), 0 (equal), undefined (parse error).
 *
 * Mirrors the compareTimes() function that used moment internally.
 */
export const compareTimes = (time1, time2) => {
  if (!time1 || !time2) return undefined;
  const t1 = parseTimeAny(time1);
  const t2 = parseTimeAny(time2);
  if (!t1 || !t2) return undefined;
  if (isBefore(t1, t2)) return -1;
  if (isAfter(t1, t2)) return 1;
  if (isEqual(t1, t2)) return 0;
  return undefined;
};

/**
 * Days between two Dates.  Positive = end is after start.
 *
 * Mirrors: end.diff(start, "days")
 */
export const diffInDays = (start, end) => differenceInDays(end, start);

// Re-export the date-fns primitives that callers may need directly
export { isBefore, isAfter, isEqual, isValid };
